import { gql, useQuery, useSubscription } from '@apollo/client';
import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { useFeatureGate } from './useFeatureGates';
import { useWsReconnectEffect } from './useWsReconnectEffect';

import type {
  BankConnectionSyncUpdatedSubscription,
  BankConnectionSyncUpdatedSubscriptionVariables,
  GetBankConnectionsSyncQuery,
  GetBankConnectionsSyncQueryVariables,
} from './__generated__/useBankConnectionSync.gql';

/**
 * One fragment for both the poll query and the subscription: the push payload
 * must land on the same normalized `BankConnection` fields the poll reads, or
 * the two silently desync the moment a field is added to one and not the other.
 */
const BANK_CONNECTION_SYNC_FIELDS = gql`
  fragment BankConnectionSyncFields on BankConnection {
    id
    lastSyncedAt
    historicalSyncComplete
    hasPotentialSyncIssue
  }
`;

const GET_BANK_CONNECTIONS_SYNC = gql`
  query GetBankConnectionsSync {
    getBankConnectionsForUser {
      ...BankConnectionSyncFields
    }
  }
  ${BANK_CONNECTION_SYNC_FIELDS}
`;

/**
 * The payload lands on the normalized `BankConnection` entity, so every
 * consumer of the poll query re-renders with the fresh sync state — no
 * explicit cache update needed. Connections ADDED after mount still arrive
 * via polling; the subscription only updates entities the cache already holds.
 */
const BANK_CONNECTION_SYNC_SUBSCRIPTION = gql`
  subscription BankConnectionSyncUpdated {
    bankConnectionSyncUpdated {
      ...BankConnectionSyncFields
    }
  }
  ${BANK_CONNECTION_SYNC_FIELDS}
`;

// Polling intervals in milliseconds
const INITIAL_POLL_INTERVAL = 5 * 1000; // 5 seconds
const BACKOFF_POLL_INTERVAL = 10 * 60 * 1000; // 10 minutes
const INITIAL_POLL_DURATION = 2 * 60 * 1000; // 2 minutes
const MAX_POLL_DURATION = 24 * 60 * 60 * 1000; // 24 hours

export const useBankConnectionSync = () => {
  const [pollStartTime, setPollStartTime] = React.useState<number | null>(null);
  const [pollInterval, setPollInterval] = React.useState<number | undefined>(undefined);
  const hasBackedOffRef = React.useRef(false);
  const hasHitMaxDurationRef = React.useRef(false);

  // Server-pushed sync state, additive over polling: between polls (10 min
  // once backed off) a finished sync cycle updates the banner immediately.
  // Skipped when the gate is off, so the lazy ws link never opens a socket
  // for ungated viewers. Delivery is at-most-once with no replay, so the poll
  // loop below is the permanent reconciliation floor, not a fallback.
  const subscriptionsEnabled = useFeatureGate(FeatureGate.GraphqlSubscriptions);
  // Mirrors `pollInterval` for the onData handler below, which must read the
  // current interval without re-subscribing on every poll-state change.
  const pollIntervalRef = React.useRef<number | undefined>(undefined);

  const { data, stopPolling, startPolling, refetch } = useQuery<
    GetBankConnectionsSyncQuery,
    GetBankConnectionsSyncQueryVariables
  >(GET_BANK_CONNECTIONS_SYNC, {
    pollInterval,
    fetchPolicy: 'network-only',
  });

  useSubscription<BankConnectionSyncUpdatedSubscription, BankConnectionSyncUpdatedSubscriptionVariables>(
    BANK_CONNECTION_SYNC_SUBSCRIPTION,
    {
      skip: !subscriptionsEnabled,
      // A received event proves the push path is live and the cache fresh, so
      // restart the poll timer — polling stays the reconciliation floor, but
      // it doesn't need to fire right after a push already delivered.
      onData: () => {
        if (pollIntervalRef.current !== undefined) {
          stopPolling();
          startPolling(pollIntervalRef.current);
        }
      },
      onError: (error) => {
        logger.warn(`[bank-connection-sync] subscription error: ${String(error)}`);
      },
    }
  );

  useWsReconnectEffect(subscriptionsEnabled, () => {
    void refetch().catch((error: unknown) => {
      logger.warn(`[bank-connection-sync] reconnect refetch failed: ${String(error)}`);
    });
  });

  React.useEffect(() => {
    pollIntervalRef.current = pollInterval;
  }, [pollInterval]);

  const bankConnections = data?.getBankConnectionsForUser ?? [];
  const hasUnsyncedConnections = bankConnections.some((connection) => connection.lastSyncedAt === null);
  const hasIncompleteHistoricalSync = bankConnections.some((connection) => !connection.historicalSyncComplete);
  const isSyncing = hasUnsyncedConnections || hasIncompleteHistoricalSync;

  const hasPotentialSyncIssue = bankConnections.some((connection) => connection.hasPotentialSyncIssue);

  // Initialize polling when we detect unsynced connections
  React.useEffect(() => {
    if (isSyncing && pollStartTime === null && !hasHitMaxDurationRef.current) {
      // Start polling with initial interval
      const startTime = Date.now();
      setPollStartTime(startTime);
      setPollInterval(INITIAL_POLL_INTERVAL);
      hasBackedOffRef.current = false;
    } else if (!isSyncing && pollStartTime !== null) {
      // Stop polling when all connections are synced
      stopPolling();
      setPollStartTime(null);
      setPollInterval(undefined);
      hasBackedOffRef.current = false;
      hasHitMaxDurationRef.current = false; // Reset so we can poll again if new unsynced connections appear
    }
  }, [isSyncing, pollStartTime, stopPolling]);

  // Manage polling interval based on elapsed time
  // Check elapsed time whenever we get new data or when polling starts
  React.useEffect(() => {
    if (pollStartTime === null || !isSyncing) {
      return;
    }

    const checkElapsedTime = () => {
      const elapsed = Date.now() - pollStartTime;

      // After initial duration, back off to longer intervals
      if (elapsed >= INITIAL_POLL_DURATION && !hasBackedOffRef.current) {
        hasBackedOffRef.current = true;
        setPollInterval(BACKOFF_POLL_INTERVAL);
      }

      // Stop polling after max duration
      if (elapsed >= MAX_POLL_DURATION) {
        stopPolling();
        setPollStartTime(null);
        setPollInterval(undefined);
        hasBackedOffRef.current = false;
        hasHitMaxDurationRef.current = true; // Prevent restarting
      }
    };

    // Check immediately
    checkElapsedTime();

    // Set up interval to check elapsed time periodically
    // This ensures we transition to backoff mode even if queries are slow
    const checkInterval = setInterval(checkElapsedTime, 1000);

    return () => {
      clearInterval(checkInterval);
    };
  }, [pollStartTime, isSyncing, stopPolling]);

  return { hasUnsyncedConnections, hasIncompleteHistoricalSync, hasPotentialSyncIssue };
};
